/**
 * Экран не гаснет, пока идёт запись (решение владельца §5.3, 12.09.2026).
 *
 * 🔴 ЗАЧЕМ. Ученик говорит две минуты, не касаясь экрана; телефон гасит его сам,
 * а вместе с экраном может уйти и микрофон.
 *
 * ⚠️ ОТКАЗ — НЕ ОШИБКА, И ОБЕЩАНИЙ МЫ НЕ ДАЁМ. Замка нет в части браузеров, а в
 * приложении Capacitor он не гарантирован вовсе. Там, где его нет, запись идёт
 * как раньше — молча, без пугающих надписей. Нативный плагин «не гасить экран»
 * сюда не тянем: он меняет мобильную сборку, а это чужая полоса.
 *
 * ⚠️ ЗАМОК СНИМАЕТСЯ САМ, КАК ТОЛЬКО ВКЛАДКУ СПРЯТАЛИ. Без повторного запроса на
 * возврате экран гаснет у всех, кто на секунду переключился и вернулся, — то есть
 * ровно в том случае, ради которого всё и делалось.
 */

type Sentinel = { release(): void | Promise<void> };
type WakeNav = { wakeLock?: { request(type: 'screen'): Promise<Sentinel> } };

export type WakeLockDeps = { nav?: WakeNav; doc?: Document };

export function keepScreenAwake(deps: WakeLockDeps = {}): () => void {
  const nav = deps.nav ?? (typeof navigator === 'undefined' ? undefined : (navigator as unknown as WakeNav));
  const doc = deps.doc ?? (typeof document === 'undefined' ? undefined : document);
  const request = nav?.wakeLock?.request;
  if (typeof request !== 'function') return () => {};

  let stopped = false;
  let held: Sentinel | null = null;

  const ask = () => {
    if (stopped) return;
    try {
      request.call(nav!.wakeLock, 'screen').then(
        (sentinel) => {
          if (stopped) {
            try {
              void sentinel.release();
            } catch {
              /* уже отпущен */
            }
            return;
          }
          held = sentinel;
        },
        () => {
          /* система не дала — живём без замка */
        },
      );
    } catch {
      /* не дала и синхронно */
    }
  };

  const onVisible = () => {
    if (!stopped && doc?.visibilityState !== 'hidden') ask();
  };

  ask();
  doc?.addEventListener('visibilitychange', onVisible);

  return () => {
    if (stopped) return;
    stopped = true;
    doc?.removeEventListener('visibilitychange', onVisible);
    try {
      void held?.release();
    } catch {
      /* уже отпущен */
    }
    held = null;
  };
}
